import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useSelector, useDispatch } from 'react-redux';
import Header from 'Components/Header/Header';
import { getMenus, getMyAccessibleSubApplications } from 'Chores/request';
import GP_LOGO from '../../Public/Image/Logo.svg';
import { saveToken } from 'Slices/App/appSlice';
import { addMenus, addLoginUserId } from 'Slices/commonSlice';
import {
  clearBillingMarketCodes
} from 'Slices/BillingAnalysis/billingAnalysisSlice';
import { VegaFlex, VegaLeftNavGroup, VegaLeftNavLink, VegaInput, VegaLeftNav, VegaBox, VegaLeftNavSection } from '@heartlandone/vega-react';
import ConfirmDialog from '../../Components/ConfirmDialog';
import { revertMenuTree, setSkipNextMenusRefresh } from '../../Slices/MenuManagement/menuThemeSlice';
import {
  pathReplaceHandle,
  getMenuIcon,
  filterMenuTree,
  setAllUserAvatar,
  setAllDictType,
  setUserPermissions,
  setFromlocalStorage,
  getFromlocalStorage
} from 'Chores/utils';
import { FE_ROUTES } from 'Chores/Api';
document.body.id = 'body-id';

const Layout = () => {
  const menus = useSelector((state) => state.common.menus);
  const dirty = useSelector((state) => state.menuTheme?.dirty);
  const token = useSelector((state) => state.app.token);
  const lastSaved = useSelector((state) => state.menuTheme?.lastSaved);
  const skipNextMenusRefresh = useSelector((state) => state.menuTheme?.skipNextMenusRefresh);

  const [originMenu, setOriginMenu] = useState([]);
  const [accessibleSubApps, setAccessibleSubApps] = useState([]);
  const [isShowMenuSearch, setMenuSearchShown] = useState(true);
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const dispatch = useDispatch();
  const location = useLocation();

  useLayoutEffect(() => {
    document.querySelector('vega-left-nav').open = true;
  }, []);

  useEffect(() => {
    if (keycloak.authenticated) {
      sessionStorage.setItem('token', `${keycloak.token}`);
      dispatch(saveToken(keycloak.token));
      dispatch(addLoginUserId(keycloak.idTokenParsed.sub));
      // Per spec: the Billing Analysis selection must be empty on every
      // login. Logout clears via the Header's onLoginOut handler.
      dispatch(clearBillingMarketCodes());

      setAllUserAvatar();
      setAllDictType();
      setUserPermissions(keycloak.idTokenParsed.sub);
    } else {
      setFromlocalStorage('loginRedirect', location, 20);
      navigate('/');
    }
  }, [keycloak.authenticated]);

  useEffect(() => {
    if (token) {
      const loadId = VegaLoader.load({
        containerSelector: '#body-id'
      });

      getMenus().then((response) => {
        const result = getMenuIcon(pathReplaceHandle(response));
        dispatch(addMenus(result));
        setOriginMenu(result);
        VegaLoader.close(loadId);
        const loginRedirect = getFromlocalStorage('loginRedirect');
        if (loginRedirect) {
          navigate(loginRedirect.pathname + loginRedirect.search);
        }
      });

      getMyAccessibleSubApplications().then((response) => {
        setAccessibleSubApps(Array.isArray(response) ? response : []);
      });
    }
  }, [token]);

  // Refresh menus after successful save of menu theme
  useEffect(() => {
    if (!lastSaved) return;
    if (skipNextMenusRefresh) {
      dispatch(setSkipNextMenusRefresh(false));
      return;
    }
    // Perform refresh
    getMenus().then((response) => {
      const result = getMenuIcon(pathReplaceHandle(response));
      dispatch(addMenus(result));
      setOriginMenu(result);
    });
  }, [lastSaved, dispatch]);

  const confirmDialogRef = useRef();

  const navigateWithDirtyGuard = (targetPath, projectId) => {
    const isMenuMgmt = location.pathname.includes('menu-management');
    const targetIsMenuMgmt = targetPath.includes('menu-management');
    const leavingMenuManagement = dirty && isMenuMgmt && !targetIsMenuMgmt && targetPath !== location.pathname;
    if (leavingMenuManagement && confirmDialogRef.current) {
      confirmDialogRef.current.open({
        title: 'Confirm Leave',
        message: 'You have unsaved changes on this page. Leaving will discard all changes. Are you sure you want to leave?',
        actionButtons: [
          { label: 'Cancel' },
          { label: 'Leave', onClick: () => {
            // Discard changes and navigate
            dispatch(revertMenuTree());
            if (projectId) {
              localStorage.setItem('_project', projectId);
            }
            navigate(targetPath);
          } }
        ]
      });
    } else {
      if (projectId) {
        localStorage.setItem('_project', projectId);
      }
      navigate(targetPath);
    }
  };

  const handleMenuItemClick = (item) => {
    if (!item || item?.nodeType === 1) {
      return;
    }
    if (item?.extraProperties?.component === 'poker') {
      window.open('https://poker.gprddigital.com');
    } else if (item?.extraProperties?.component === 'activityhub') {
      const token = sessionStorage.getItem('token');
      if (!token) {
        window.alert('Error, please re-login');
        return;
      }
      window.open(`${process.env.REACT_APP_ACTIVITY_HUB}?token=${token}`);
    } else {
      const targetPath = `${FE_ROUTES.HOME}${item.newPath}`;
      navigateWithDirtyGuard(targetPath, item.extraProperties?.projectId);
    }
  };

  const renderMenuData = (data = [], level = 0) => {
    return data.map((item) => {
      const isCategory = item?.nodeType === 1;
      const isLinkNode = item?.nodeType === 3 || item?.nodeType === 2;
      // Top-level: use VegaLeftNavSection
      if (level === 0) {
        return (
          <VegaLeftNavSection
            key={item.key}
            sectionHeader={{ title: item.label }}
          >
            {item?.children?.length ? (
              renderMenuData(item.children, level + 1)
            ) : (
              <VegaLeftNavLink
                selected={location.pathname.replace(FE_ROUTES.HOME, '') === item.newPath}
                icon={item.icon}
                className={isCategory ? 'category-link' : ''}
                onVegaMenuClick={() => !isCategory && handleMenuItemClick(item)}
              >
                {item.label}
              </VegaLeftNavLink>
            )}
          </VegaLeftNavSection>
        );
      }
      // Nested groups (legacy behavior)
      if (item?.children?.length) {
        return (
          <VegaLeftNavGroup icon={item.icon} key={item.key} label={item.label}>
            {renderMenuData(item.children, level + 1)}
          </VegaLeftNavGroup>
        );
      } else if (isLinkNode || isCategory) {
        return (
          <VegaLeftNavLink
            selected={location.pathname.replace(FE_ROUTES.HOME, '') === item.newPath}
            icon={item.icon}
            customSelectionEnabled={true}
            className={isCategory ? 'category-link' : ''}
            onVegaMenuClick={() => {
              if (!isCategory) {
                handleMenuItemClick(item);
              }
            }}
            key={item.key}
          >
            {item.label}
          </VegaLeftNavLink>
        );
      }
      return null;
    });
  };

  const handleMenuSearch = (e) => {
    const res = filterMenuTree(e.target.value, originMenu);
    dispatch(addMenus(res));
  };
  return (
    <VegaBox className="App">
      <VegaLeftNav
        footnote={`Version 4.0.0.0`}
        showAsOverlay={false}
        onVegaStateUpdate={(e) => {
          setMenuSearchShown(e.detail === 'close' ? false : true);
        }}
      >
        <div slot="header-slot">
          <img src={GP_LOGO} style={{ width: '100%' }}></img>
        </div>

        <div className={isShowMenuSearch ? '' : 'v-hidden'}>
          <VegaInput
            id="menuSearch"
            autoComplete="off"
            prefixIcon="search"
            showClearIcon="true"
            size="small"
            placeholder="Search"
            onVegaChange={(e) => handleMenuSearch(e)}
          ></VegaInput>
        </div>
        {renderMenuData(menus, 0)}
        {accessibleSubApps.length > 0 && (
          <VegaLeftNavSection sectionHeader={{ title: 'Sub Applications' }}>
            {accessibleSubApps.map((subApp) => (
              <VegaLeftNavLink
                key={subApp.menuName}
                icon="fas fa-up-right-from-square"
                onVegaMenuClick={() => window.open(subApp.subApplicationUrl)}
              >
                {subApp.menuName}
              </VegaLeftNavLink>
            ))}
          </VegaLeftNavSection>
        )}
      </VegaLeftNav>
      <VegaBox padding="size-24" className="vega-left-nav-content">
        <VegaFlex direction="col" gap="size-24" style={{ height: '100%' }}>
          <Header />
          <Outlet />
          {/* wait vega components update */}
          {/* <VegaAppFooter
            tosUrl="https://www.heartland.us/terms"
            privacyUrl="https://www.heartland.us/privacy-policy"
            showDisclaimer={false}
          ></VegaAppFooter> */}
        </VegaFlex>
      </VegaBox>
      <ConfirmDialog ref={confirmDialogRef} />
    </VegaBox>
  );
};
export default Layout;
